/**
 * «Send varsel til de påmeldte» (owner 2026-11-01): a push only to the people signed up for one event — those who
 * answered «Kommer» in the app, bought a ticket in the app, or are on the Påmelding list with their own app account
 * (and, if chosen, the waiting list). One button and one dialog, used from Påmelding and from Arrangementer.
 *
 * Counts only: «Går til N personer (M telefoner)» from GET /admin/events/:id/attendee-audience — never a name. Somebody
 * with push.approve sends it at once; otherwise it is a draft for somebody else to approve under Varsler (lib/push-send,
 * owner 2026-11-01). Who it reaches is decided again when it is sent.
 * Shown to staff with push.compose AND (events.write OR events.registrations.manage), with the push capability, for a
 * published or scheduled event (the server decides the same on every request).
 */
import React, { useEffect, useState } from 'react';
import { call, can, type Body } from '../lib/api';
import { pushButtonLabel, pushRuleNote, sendsOwnPush, submitPush } from '../lib/push-send';
import { useMsg } from '../lib/hooks';
import { useNav } from '../lib/nav';
import { Btn, Field } from './ui';
import { FormDialog } from './dialog';

const TITLE_MAX = 65, BODY_MAX = 178;

/** Whether this person may send to an event's attendees at all (the server checks again). */
export function mayPushAttendees(caps: string[] | null | undefined, status: string | null | undefined): boolean {
  return Boolean(caps && caps.includes('push')) && can('push.compose') && (can('events.write') || can('events.registrations.manage'))
    && ['published', 'scheduled'].includes(String(status || ''));
}

/** The preview's query string: only the sources ticked. */
export function attendeeQuery(s: { coming: boolean; tickets: boolean; list: boolean; waitlist: boolean }): string {
  const q = new URLSearchParams();
  if (s.coming) q.set('coming', '1');
  if (s.tickets) q.set('tickets', '1');
  if (s.list) { q.set('list', '1'); if (s.waitlist) q.set('waitlist', '1'); }
  return q.toString();
}

const people = (n: number) => `${n} ${n === 1 ? 'person' : 'personer'}`;
const phones = (n: number) => `${n} ${n === 1 ? 'telefon' : 'telefoner'}`;

export function AttendeePushButton({ event, hasList }: { event: Body; hasList?: boolean }) {
  const { caps } = useNav();
  const [open, setOpen] = useState(false);
  if (!mayPushAttendees(caps, event.status)) return null;
  return <div className="acts">
    <Btn ghost onClick={() => setOpen(true)}>Send varsel til de påmeldte</Btn>
    {open && <AttendeePushDialog event={event} hasList={hasList} ticketsOn={Boolean(caps && caps.includes('event_tickets'))} onClose={() => setOpen(false)} />}
  </div>;
}

function AttendeePushDialog({ event, hasList, ticketsOn, onClose }: { event: Body; hasList?: boolean; ticketsOn: boolean; onClose: () => void }) {
  const title0 = String(event.title || event.title_no || 'arrangementet');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [src, setSrc] = useState({ coming: true, tickets: ticketsOn, list: hasList !== false, waitlist: false });
  const [count, setCount] = useState<Body | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [, say] = useMsg();
  const q = attendeeQuery(src);
  useEffect(() => {
    let live = true;
    setCount(null); setFailed(null);
    if (!q) { setCount({ people: 0, devices: 0, ticketsWithoutAccount: 0 }); return; }
    call(`/events/${encodeURIComponent(event.id)}/attendee-audience?${q}`)
      .then((b) => { if (live) setCount(b); })
      .catch((x) => { if (live) setFailed((x as Error).message); });
    return () => { live = false; };
  }, [event.id, q]);
  const include = (['coming', 'tickets', 'list'] as const).filter((k) => src[k]);
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    const r = await submitPush({ kind: 'event', title_no: title.trim(), body_no: text.trim(),
      audience: { eventAttendees: event.id, include, waitlist: src.list && src.waitlist } }).finally(() => setBusy(false));
    say(r.text, r.tone);
    if (r.ok) onClose();
  };
  const box = (k: 'coming' | 'tickets' | 'list' | 'waitlist', label: string, disabled = false) =>
    <label className="work-check"><input type="checkbox" checked={src[k]} disabled={disabled} onChange={(e) => setSrc({ ...src, [k]: e.target.checked })} /> {label}</label>;
  const n = count ? Number(count.people) || 0 : 0;
  return <FormDialog open title="Send varsel til de påmeldte" dirty={Boolean(title || text)} onClose={onClose}
    hint={`Bare de som er påmeldt «${title0}» og har appen, får varselet. Det åpner arrangementet i appen.`}>
    <Field label="Tittel" required note={`Maks ${TITLE_MAX} tegn — lengre titler kuttes på låseskjermen.`}>{(id, d) =>
      <input id={id} aria-describedby={d} maxLength={TITLE_MAX} value={title} onChange={(e) => setTitle(e.target.value)} />}</Field>
    <Field label="Tekst" required note={`Maks ${BODY_MAX} tegn.`}>{(id, d) =>
      <textarea id={id} aria-describedby={d} rows={3} maxLength={BODY_MAX} value={text} onChange={(e) => setText(e.target.value)} />}</Field>
    <fieldset className="field"><legend>Hvem skal få det</legend>
      {box('coming', 'Kommer (svar i appen)')}
      {ticketsOn && box('tickets', 'Billetter')}
      {box('list', 'Påmeldingslisten')}
      {src.list && box('waitlist', 'også venteliste')}
    </fieldset>
    {event.status === 'scheduled' && <p className="note">Arrangementet er ikke publisert ennå: varselet {sendsOwnPush() ? 'lagres' : 'kan godkjennes'}, men sendes først når arrangementet er publisert.</p>}
    <p className="note" role="status" aria-live="polite">{failed ? `Kunne ikke telle mottakerne: ${failed}`
      : !count ? 'Teller …'
      : !include.length ? 'Velg minst én gruppe.'
      : `Går til ${people(n)} (${phones(Number(count.devices) || 0)}).`}
      {count && Number(count.ticketsWithoutAccount) > 0 && ` ${count.ticketsWithoutAccount} billettkjøp uten app-konto (i døra) kan ikke nås.`}
    </p>
    <p className="note">Bare de på listen som har koblet sin egen app-konto, kan nås. {pushRuleNote()}</p>
    <div className="acts"><Btn disabled={busy || !title.trim() || !text.trim() || !include.length || !count || n === 0} aria-busy={busy || undefined} onClick={submit}>{busy ? 'Sender …' : pushButtonLabel()}</Btn>
      <Btn ghost onClick={onClose}>Avbryt</Btn></div>
  </FormDialog>;
}
